// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import RoundGameInfoModal from '@/components/RoundGameInfoModal';
import { useUser } from '@/app/user-context';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import { ladderForWeight, meritRowForWeight } from '@/features/game-definitions/merits';
import { useGameMeritLadders } from '@/features/game-definitions/useGameMeritLadders';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import { card } from '../../typography';
import GameTile from '@/features/game-selection/GameTile';
import GroupedGameGrid from '@/features/game-selection/GroupedGameGrid';
import MeritDistributionBar from '@/features/game-selection/MeritDistributionBar';
import { cycleWeightPercent, normalizeWeight } from '@/features/game-selection/weights';
import { resolveSelectedRuleSetId } from '@/features/game-selection/ruleSets';
import SectionHeading from '../shared/SectionHeading';
import { TOUR_EDIT_MEDIA_PAGE_PATH } from '../../constants';
import type { GameDefinition } from '../../types';
import { normalizeGameId } from '../../utils';
import { isTournamentOnlyGame } from '@/features/game-definitions/tournamentOnly';

type Props = {
  roundId: string;
  enabledGameDefinitions: GameDefinition[];
  roundGameIds: string[];
  weightsByGameId: Record<string, number>;
  ruleSetByGameId?: Record<string, string>;
  tournamentGameIds: string[];
  tournamentWeightsByGameId: Record<string, number>;
  tournamentRuleSetByGameId?: Record<string, string>;
  playerCount: number;
  /** The tour's roster, used to size the ladders while the round's own field is unknown. */
  tourPlayerCount?: number;
  /** The round's merit boost; the previewed ladders arrive already boosted. */
  meritBoostPercent?: number;
  onUpdateRoundGames: (roundId: string, gameIds: string[]) => void;
  onWeightChange: (roundId: string, gameId: string, weightPercent: number) => void;
  onRuleSetChange?: (roundId: string, gameId: string, ruleSetId: string) => void;
};

/**
 * A round whose player list has not arrived — or has not been filled — cannot say what its games
 * pay. This used to stand in a flat 4, which renders as a ladder indistinguishable from a real
 * four-player round: a tour of eight showed Stableford at 8 / 6 / 4 / 3 / 2 / 1 / 0 / 0 while
 * each of its rounds advertised 8 / 4 / 1 / 0. The tour's roster is the honest stand-in, since
 * that is the field a round is scheduled with; with neither, the tiles show no ladder at all.
 */
const UNKNOWN_FIELD = 0;

export default function RoundGamesEditor({
  roundId,
  enabledGameDefinitions: allGameDefinitions,
  roundGameIds,
  weightsByGameId,
  ruleSetByGameId,
  tournamentGameIds,
  tournamentWeightsByGameId,
  tournamentRuleSetByGameId,
  playerCount,
  tourPlayerCount = 0,
  meritBoostPercent = 100,
  onUpdateRoundGames,
  onWeightChange,
  onRuleSetChange,
}: Props) {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { user } = useUser();
  const { t } = useI18n();
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoGame, setInfoGame] = useState<GameDefinition | null>(null);

  // Tour-only games (Eclectic, Freeze) are not round games; they are managed in
  // the tour games editor and never appear here, not even as locked tiles.
  const enabledGameDefinitions = useMemo(
    () => allGameDefinitions.filter((game) => !isTournamentOnlyGame(game)),
    [allGameDefinitions],
  );

  const roundGameSet = useMemo(() => new Set(roundGameIds.map(normalizeGameId)), [roundGameIds]);
  const tournamentGameSet = useMemo(
    () => new Set(tournamentGameIds.map(normalizeGameId)),
    [tournamentGameIds],
  );
  const meritPlayerCount = playerCount > 1
    ? playerCount
    : (tourPlayerCount > 1 ? tourPlayerCount : UNKNOWN_FIELD);
  // The ladders arrive resolved per preset weight; the round and its tour may carry others.
  const extraMeritWeights = useMemo(
    () => [...Object.values(weightsByGameId), ...Object.values(tournamentWeightsByGameId)],
    [tournamentWeightsByGameId, weightsByGameId],
  );
  const meritLadders = useGameMeritLadders(apiBaseUrl, user?.token, meritPlayerCount, 1, extraMeritWeights, meritBoostPercent);

  const getWeightPercent = useCallback(
    (game: GameDefinition) => {
      if (tournamentGameSet.has(normalizeGameId(game.id))) {
        return tournamentWeightsByGameId[game.id] ?? normalizeWeight(game.defaultWeightPercent);
      }
      return weightsByGameId[game.id] ?? normalizeWeight(game.defaultWeightPercent);
    },
    [tournamentGameSet, tournamentWeightsByGameId, weightsByGameId],
  );

  const cycleWeight = useCallback(
    (game: GameDefinition) => {
      if (tournamentGameSet.has(normalizeGameId(game.id))) return;
      onWeightChange(roundId, game.id, cycleWeightPercent(getWeightPercent(game)));
    },
    [getWeightPercent, onWeightChange, roundId, tournamentGameSet],
  );

  const meritDistributionEntries = useMemo(
    () =>
      enabledGameDefinitions
        .filter((game) => {
          const normalized = normalizeGameId(game.id);
          return roundGameSet.has(normalized) || tournamentGameSet.has(normalized);
        })
        .map((game) => {
          const row = meritRowForWeight(meritLadders[game.id], getWeightPercent(game));
          return {
            gameId: game.id,
            name: getGameDisplayName(game),
            ladder: row?.ladder ?? [],
            winnerTotal: row?.winnerTotal ?? 0,
            tourTotal: row?.tourTotal ?? null,
          };
        }),
    [enabledGameDefinitions, getWeightPercent, meritLadders, roundGameSet, tournamentGameSet],
  );

  const handleToggle = useCallback(
    (gameId: string) => {
      const normalizedId = normalizeGameId(gameId);
      if (tournamentGameSet.has(normalizedId)) return;

      const currentRoundOnly = roundGameIds.filter((id) => !tournamentGameSet.has(normalizeGameId(id)));
      if (roundGameSet.has(normalizedId)) {
        onUpdateRoundGames(
          roundId,
          currentRoundOnly.filter((id) => normalizeGameId(id) !== normalizedId),
        );
      } else {
        onUpdateRoundGames(roundId, [...currentRoundOnly, gameId]);
      }
    },
    [roundId, roundGameIds, roundGameSet, tournamentGameSet, onUpdateRoundGames],
  );

  const openInfo = useCallback((game: GameDefinition) => {
    setInfoGame(game);
    setInfoOpen(true);
  }, []);

  // The setup modal shows the same ladder the tile does, resolved for the weight in force.
  const infoGameMerits = infoGame ? meritLadders[infoGame.id] : undefined;
  const infoGameLadder = infoGame ? ladderForWeight(infoGameMerits, getWeightPercent(infoGame)) : [];

  const renderTile = useCallback(
    (game: GameDefinition) => {
      const normalized = normalizeGameId(game.id);
      const isTourGame = tournamentGameSet.has(normalized);
      const isSelected = isTourGame || roundGameSet.has(normalized);
      const weightPercent = getWeightPercent(game);
      const ladder = isSelected ? ladderForWeight(meritLadders[game.id], weightPercent) : [];
      return (
        <GameTile
          key={game.id}
          game={game}
          selected={isSelected}
          mediaPagePath={TOUR_EDIT_MEDIA_PAGE_PATH}
          locked={isTourGame}
          lockedLabel={isTourGame ? t('tourEdit.roundGameLockedTour') : undefined}
          onPress={() => handleToggle(game.id)}
          onInfoPress={() => openInfo(game)}
          meritLadder={ladder}
          perTeam={meritLadders[game.id]?.perTeam ?? false}
          perAchievement={meritLadders[game.id]?.kind === 'perAchievement'}
          perEvent={meritLadders[game.id]?.kind === 'perEvent'}
          weightFixed={meritLadders[game.id] !== undefined && meritLadders[game.id].kind !== 'ladder'}
          weightPercent={weightPercent}
          weightLocked={isTourGame}
        />
      );
    },
    [
      getWeightPercent,
      handleToggle,
      openInfo,
      meritLadders,
      roundGameSet,
      t,
      tournamentGameSet,
    ],
  );

  return (
    <View style={styles.container}>
      <SectionHeading title={t('tourEdit.roundGamesTitle')} count={roundGameIds.length} />
      <Text style={styles.hint}>{t('tourEdit.roundGamesHint')}</Text>
      <MeritDistributionBar entries={meritDistributionEntries} />
      {enabledGameDefinitions.length === 0 ? (
        <Text style={styles.emptyText}>{t('tourEdit.gamesEmpty')}</Text>
      ) : (
        <GroupedGameGrid games={enabledGameDefinitions} renderTile={renderTile} />
      )}
      <RoundGameInfoModal
        visible={infoOpen}
        game={infoGame}
        apiBaseUrl={apiBaseUrl}
        onClose={() => setInfoOpen(false)}
        weightPercent={infoGame ? getWeightPercent(infoGame) : undefined}
        onWeightPress={infoGame ? () => cycleWeight(infoGame) : undefined}
        weightLocked={infoGame ? tournamentGameSet.has(normalizeGameId(infoGame.id)) : false}
        weightFixed={infoGameMerits !== undefined && infoGameMerits.kind !== 'ladder'}
        meritLadder={infoGameLadder}
        perTeam={infoGameMerits?.perTeam ?? false}
        perAchievement={infoGameMerits?.kind === 'perAchievement'}
        perEvent={infoGameMerits?.kind === 'perEvent'}
        selectedRuleSetId={
          infoGame
            ? resolveSelectedRuleSetId({
                ruleSets: infoGame.rules,
                roundRuleSetId: ruleSetByGameId?.[infoGame.id],
                tourRuleSetId: tournamentRuleSetByGameId?.[infoGame.id],
              })
            : null
        }
        tourRuleSetId={infoGame ? tournamentRuleSetByGameId?.[infoGame.id] : null}
        // Deliberately offered for tour-locked games too, unlike the weight control: a tour game's
        // weight is tour-managed, but its rule set is only a tour *default* the round may override.
        onSelectRuleSet={
          infoGame && onRuleSetChange ? (ruleSetId) => onRuleSetChange(roundId, infoGame.id, ruleSetId) : undefined
        }
        ruleScope="round"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    ...card,
    gap: 8,
  },
  hint: {
    fontSize: 12,
    color: appColors.text.muted,
    fontStyle: 'italic',
    paddingHorizontal: 4,
  },
  emptyText: {
    fontSize: 14,
    color: appColors.text.muted,
    fontStyle: 'italic',
    paddingVertical: 12,
  },
});
